import React, {useCallback, useEffect, useState} from "react";
import translate from "components/translations/translations.wrapper";
import {Controller, useForm} from "react-hook-form";
import SettingsPreferencesCard from "components/newSettings/Settings.preferences.card";
import {getNotifications} from "components/newSettings/Settings.notifications.fetch";
import * as Api from "utils/api/api";
import {
    DateHourType, NotificationType,
    EVENT_TYPES, DAYS_OF_THE_WEEK_NUMBER, DAYS_OF_THE_WEEK,
    USER_TYPE, USER_TYPE_KEYS,
    SETTINGS_AVAILABLE_HOURS, DAYS_HOURS, DAY_TIME_KEYS
} from "types/settings";
import ErrorInContext from "components/common/errorInContext";
import debounce from "lodash/debounce";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import * as reduxSelectors from "components/store/application.reducers";
import {loadUserData} from "components/usercontext/user.action";
import Checkbox from "components/common/checkbox.component";


const NotificationsContent = (props: any): React.ReactElement => {

    const {control} = useForm({defaultValues: null});
    const [notifications, setNotifications] = useState<NotificationType | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = React.useState<{ title: string, text: string, more?: string | null } | null>(null);
    const context = useSelector(reduxSelectors.getUsersCurrentContextId);

    const dispatch = useDispatch();

    const debouncedSave = useCallback(
        debounce(async (data: any) => {
            await saveNotifications(data);
        }, 1000),
        []
    );


    const saveNotifications = async (data: any) => {
        if (loading || _.isEqual(data, notifications)) return; // Prevent multiple submissions

        const eventMail = parseEmailSettings(data);
        const digestMail = parseDateTimeNotifications(data.dateTime.approver, data.dateTime.admin, data.overdueRemainder);

        setLoading(true);
        try {
            console.log("saving...");

            await Api.updateEmailSettings({
                eventMail: eventMail || [],
                digestMail: digestMail,
                userAdmin: data.isUserAdmin || false,
                userApprover: data.isUserApprover || false,
            });

        } catch (error) {
            console.error("Error saving notifications:", error);
            setError({
                title: props.translate("settings.preferences.saveErrorTitle"),
                text: props.translate("settings.preferences.saveErrorText"),
                more: error.errorMessages || null
            });
        } finally {
            setLoading(false);
        }
    }


    useEffect(() => {
        getNotifications().then((response) => {
            setNotifications(response);
        });
    }, [context]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);


    useEffect(() => {
        dispatch(loadUserData());
    }, []);

    const getDayTime = (dayTime: {
        days: { [s: string]: unknown; } | ArrayLike<unknown>;
        hours: { [s: string]: unknown; } | ArrayLike<unknown>;
    }) => {
        return Object.entries(dayTime.days)
            .filter(([_, isSelected]) => isSelected)
            .flatMap(([day]) =>
                Object.entries(dayTime.hours)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([hour]) => ({
                        dayOfWeek: DAYS_OF_THE_WEEK_NUMBER[day],
                        hourOfDay: parseInt(hour, 10) || 9
                    }))
            );
    }

    const parseDateTimeNotifications = (approverEvents: DateHourType, adminEvents: DateHourType, overdueRemainder: boolean) => {

        const parsedApproverEvents = getDayTime(approverEvents);
        const parsedAdminEvents = getDayTime(adminEvents);

        const digestMail = [];
        if (parsedApproverEvents.length > 0) {
            digestMail.push({
                digestMailType: EVENT_TYPES.EMAIL_EVENTS, // EMAIL_EVENTS
                events: parsedApproverEvents,
                mailEnabled: true,
            });
        }

        if (parsedAdminEvents.length > 0) {
            digestMail.push({
                digestMailType: EVENT_TYPES.ADMIN_EMAIL_EVENTS, // ADMIN_EMAIL_EVENTS
                events: parsedAdminEvents,
                mailEnabled: true,
            });
        }

        if (overdueRemainder) {
            digestMail.push({
                digestMailType: EVENT_TYPES.EMAIL_ENABLED, // EMAIL_ENABLED
                events: [],
                mailEnabled: true,
            });
        }

        return digestMail;
    }

    const parseEmailSettings = (data: NotificationType) => {
        return [
            data.newTaskForApprover && 1,
            data.appointedAsSubstitute && 6,
            data.taskBlocked && 2,
            data.documentBlocked && 7,
        ].filter(Boolean);
    }

    const handleChange = async (onChange: (value: any) => void, e: React.MouseEvent<HTMLLabelElement>, name: string) => {
        e.stopPropagation();
      //  e.preventDefault();
        const isChecked =e.currentTarget.querySelector("input").checked;// !notifications[name];
        onChange(isChecked);
        const newData = {...notifications, [name]: isChecked};

        setNotifications(newData);
        await debouncedSave(newData);
    };


    const handleDayTimeChange = async (
        type: DAY_TIME_KEYS,
        userType: USER_TYPE_KEYS,
        onChange: (value: any) => void,
        e: React.ChangeEvent<HTMLInputElement>,
        name: string
    ) => {
        e.stopPropagation();
       // e.preventDefault();


        const key = type || DAYS_HOURS.DAYS
        const newValue = name.replace("_" + userType, "");
        const isChecked =e.currentTarget.querySelector("input").checked; // !notifications.dateTime[userType][type][newValue];

        onChange(isChecked);

        const updatedApprovalDayTime = {
            ...notifications.dateTime[userType],
            [key]: {...notifications.dateTime[userType][key], [newValue]: isChecked},
        };

        const newData = {
            ...notifications,
            dateTime: {...notifications.dateTime, [userType]: updatedApprovalDayTime},
        };

        setNotifications(newData);
        await debouncedSave(newData);
    };

    const renderCheckbox = (name: string, label: string, checked: boolean, changeHandle = handleChange, labelClass: string = "") => (
        <Controller
            name={name}
            control={control}
            render={({field: {onChange, onBlur}}) => (
                <div className={"my-1"}>
                    <Checkbox
                        inputId={name}
                        onChange={(e) => changeHandle(onChange, e, name)}
                        isDisabled={loading}
                        isChecked={checked}
                        aria-label={label}
                        label={label}
                    />
                </div>
            )}
        />
    );

    const renderDayCheckboxes = (prefix: string, days: any, changeHandle: any) =>
        DAYS_OF_THE_WEEK.map((day) => {
                return <div className={"d-table-cell pe-4"} key={`${day}_${prefix}`}>
                    {renderCheckbox(`${day}_${prefix}`, props.translate(`settings.${day}`), days[day] || false, changeHandle,
                        "d-table-cell pe-4")}
                </div>
            }
        )
    ;

    const renderHourCheckboxes = (prefix: string, hours: any, changeHandle: any) =>
        SETTINGS_AVAILABLE_HOURS.map((hour) => {
                return <div className={"d-table-cell pe-4"} key={`${hour}_${prefix}`}>
                    {renderCheckbox(`${hour}_${prefix}`, hour + ":00", hours[hour] || false, changeHandle, "d-table-cell pe-4")}
                </div>
            }
        );

    const approverEmailNotifications = () =>
        notifications && (
            <div className={"d-flex flex-column"}>
                {renderCheckbox("newTaskForApprover",
                    props.translate("settings.notifications.newTask"),
                    notifications.newTaskForApprover
                )}
                {renderCheckbox(
                    "appointedAsSubstitute",
                    props.translate("settings.notifications.appointedSubstitute"),
                    notifications.appointedAsSubstitute
                )}
                <h4 className={"mt-16 mb-8"}>{props.translate("settings.notifications.aReminder")}</h4>
                {renderCheckbox(
                    "overdueRemainder",
                    props.translate("settings.notifications.soonOverdue"),
                    notifications.overdueRemainder
                )}
                <h4 className={"mt-16 mb-8"}>{props.translate("settings.notifications.reminderTasksOn")}</h4>
                <div className={"d-table"}>
                    <div className={"d-table-row"}>
                        {renderDayCheckboxes("approver",
                            props.translate(notifications.dateTime.approver.days),
                            handleDayTimeChange.bind(null, DAYS_HOURS.DAYS, USER_TYPE.APPROVER))}</div>
                    <div className={"notifications_multi-cell"}>
                        <h4 className={"mt-16 mb-8 text-nowrap"}>{props.translate("settings.notifications.sentForEachSelectedDay")}</h4>
                    </div>
                    <div className={"d-table-row"}>
                        {renderHourCheckboxes("approver",
                            props.translate(notifications.dateTime.approver.hours),
                            handleDayTimeChange.bind(null, DAYS_HOURS.HOURS, USER_TYPE.APPROVER))}</div>
                </div>
            </div>
        );

    const adminEmailNotifications = () =>
        notifications && (
            <div className={"d-flex flex-column"}>
                <h4 className={"mb-8"}>{props.translate("settings.notifications.emailNotification")}</h4>
                {renderCheckbox("taskBlocked",
                    props.translate("settings.notifications.blockedTask"),
                    notifications.taskBlocked)}
                {renderCheckbox("documentBlocked",
                    props.translate("settings.notifications.blockedDocument"),
                    notifications.documentBlocked)}
                <h4 className={"mt-16 mb-8"}>{props.translate("settings.notifications.adminReminderTasksOn")}</h4>
                <div className={"d-table"}>
                    <div className={"d-table-row"}>
                        {renderDayCheckboxes("admin",
                            props.translate(notifications.dateTime.admin.days),
                            handleDayTimeChange.bind(null, DAYS_HOURS.DAYS, USER_TYPE.ADMIN))}</div>
                    <div className={"notifications_multi-cell"}>
                        <h4 className={"mt-10 mb-8 text-nowrap"}>{props.translate("settings.notifications.sentForEachSelectedDay")}</h4>
                    </div>
                    <div className={"d-table-row"}>
                        {renderHourCheckboxes("admin",
                            props.translate(notifications.dateTime.admin.hours),
                            handleDayTimeChange.bind(null, DAYS_HOURS.HOURS, USER_TYPE.ADMIN))}</div>
                </div>
            </div>
        );


    return (
        <form>
            {error && <ErrorInContext {...error}
                                      onCloseCallback={() => {
                                          setError(null)
                                      }}
            />}
            {notifications?.isUserApprover && (
                <SettingsPreferencesCard
                    title={props.translate("settings.notifications.companyEmail")}
                    subtitle={props.translate("settings.notifications.emailNotification")}
                    contentInput={approverEmailNotifications()}
                />
            )}
            {notifications?.isUserAdmin && (
                <SettingsPreferencesCard
                    title={props.translate("settings.notifications.adminCompanyEmail")}
                    contentInput={adminEmailNotifications()}
                />
            )}
        </form>
    );
};

export default translate(NotificationsContent);